import styled, { css } from 'styled-components';

import { breakpoint } from 'DesignLibrary/global';
import {
  FadeInKeyframes,
  FadeInDownKeyframes,
  FadeInLeftKeyframes,
  FadeOutUpKeyframes,
  FadeInRightKeyframes,
  FadeOutKeyframes,
} from 'DesignLibrary/keyframes';

interface HeroContentProps {
  isNextClicked: boolean;
}

export const HeroContainer = styled.section<HeroContentProps>`
  ${({ isNextClicked }) =>
    isNextClicked &&
    css`
      .hero-text-note {
        opacity: 1;
        animation-name: ${FadeOutKeyframes};
        animation-duration: 1s;
        animation-delay: 0s;
        -webkit-animation-fill-mode: forwards;
        animation-fill-mode: forwards;
      }
    `}

  .hero-text-note {
    background: white;
    border-color: var(--colors-yellow-200);
    border-radius: 0.5rem;
    padding: 1rem;
    width: 100%;
    opacity: 0;
    animation: ${FadeInLeftKeyframes} 1s 1.75s forwards;
    /* Reset typography styles to allow ContentfulTextNote styling to apply */
    h2,
    h3,
    h4,
    h5,
    ul,
    ol,
    li,
    p {
      color: inherit;
      font-family: inherit;
      font-size: inherit;
      font-weight: inherit;
      line-height: inherit;
      margin-bottom: inherit;
      opacity: 1;
      animation: none;
    }
  }

  h2 {
    font-family: 'Gelica', Georgia, serif;
    font-weight: 400;
    font-size: 2.5rem;
    margin-bottom: 1.5rem;
    line-height: 1.2;

    ${breakpoint.TABLET} {
      font-size: 3.75rem;
    }
  }

  .cta-buttons {
    opacity: 0;
    animation: ${FadeInLeftKeyframes} 1s 1.75s forwards;
    width: 100%;
    margin-bottom: 3.5rem;

    .paragraph {
      display: none;
    }

    .chakra-stack {
      display: flex;
      flex-direction: column;
      gap: 1.5rem;

      ${breakpoint.WIDEDESKTOP} {
        flex-direction: row;
      }
    }
  }

  .carousel-outer {
    opacity: 0;
    animation: ${FadeInKeyframes} 1s 2.25s forwards;
  }

  .pingdom-get-started,
  .button.primary {
    padding: 1rem 2rem 1.1rem;
    font-size: 1.25rem;
    font-weight: 400 !important;
    background: var(--colors-alex-gold80);
    border: 3px solid var(--colors-alex-gold90);
    border-bottom-width: 8px;
    border-radius: 0.5rem;
    box-shadow:
      0px 10px 10px 0px rgba(0, 0, 0, 0.06),
      0px 0px 12px 0px rgba(0, 0, 0, 0.1);
    height: auto;
    max-height: none;

    &:hover {
      background: var(--colors-alex-gold90);
      border-color: var(--colors-alex-gold);
    }

    &:active {
      border-bottom-width: 3px;
      background: var(--colors-alex-gold80);
      margin-top: 5px;
    }

    &.guest {
      border: 0;
      box-shadow: none;
      color: var(--colors-alex-blue);
      background: var(--colors-alex-offwhite);
      mix-blend-mode: multiply;

      &:hover,
      &:active {
        background: var(--colors-alex-gray);
      }
    }
  }

  .rounded-container {
    opacity: 0;
    animation: ${FadeInKeyframes} 1s 2.75s forwards;
    background: white;
    padding: 2rem;
    border-radius: 1rem;
    position: relative;
    box-shadow: 0px 2px 8px 0px rgba(47, 47, 82, 0.16);
    width: 100%;
    margin-bottom: 4.5rem;

    ${breakpoint.TABLET} {
      padding: 4rem 0 4rem 4rem;
      margin-bottom: 8rem;
      border-radius: 1rem 0 0 1rem;
      width: 200%;
    }

    h2 {
      color: var(--colors-art-blue);
    }

    .haw {
      display: flex;
      gap: 5rem;
      flex-direction: column;

      ${breakpoint.TABLET} {
        flex-direction: row;
        max-width: 50%;
        align-items: flex-end;
      }

      div {
        ${breakpoint.TABLET} {
          max-width: 50%;
        }
      }

      img {
        max-width: 600px;
        margin-bottom: 2rem;
        width: 100%;
      }

      p {
        font-size: 1.25rem;
        font-weight: 500;
        max-width: 600px;
      }
    }
  }

  .secondary-CTA {
    opacity: 0;
    animation: ${FadeInKeyframes} 1s 3.25s forwards;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin-bottom: 4.5rem;

    ${breakpoint.TABLET} {
      flex-direction: row;
      gap: 6rem;
      align-items: center;
      margin-bottom: 8rem;
      gap: 4rem;
    }

    img {
      max-width: 510px;
      width: 100%;
    }

    div.text {
      ${breakpoint.TABLET} {
        max-width: 56%;
      }
    }

    h2 {
      color: var(--colors-art-blue);

      span {
        background: linear-gradient(100deg, var(--colors-art-green) 20%, var(--colors-art-darkgreen) 100%);
        background-clip: text;
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
      }
    }

    p {
      color: var(--colors-gray-black);
      font-size: 1.5rem;
      line-height: 1.5;
      font-weight: 300;
      margin-bottom: 3rem;
    }
  }

  ${({ isNextClicked }) =>
    isNextClicked &&
    css`
      .rounded-container,
      .secondary-CTA {
        opacity: 1;
        animation-name: ${FadeOutKeyframes};
        animation-duration: 0.5s;
        animation-delay: 0s;
      }
    `}
`;

export const HeroContentWrapper = styled.section<HeroContentProps>`
  position: relative;
  padding: var(--sizing-sm) var(--sizing-xs) var(--sizing-2xl) var(--sizing-xs);
  display: flex;
  flex-direction: column;
  gap: var(--sizing-lg);

  ${breakpoint.TABLET} {
    display: grid;
    grid-template-columns: 1fr 1fr;
    padding: 96px 0 var(--sizing-3xl);
  }

  ${breakpoint.DESKTOP} {
    grid-template-columns: 1.2fr 1fr;
  }

  ${breakpoint.WIDEDESKTOP} {
    grid-template-columns: 1fr 1fr;
  }

  .content {
    order: 2;
    ${breakpoint.TABLET} {
      order: 1;
    }
  }

  .animation {
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    animation: ${FadeInRightKeyframes} 1s 2s forwards;
    order: 1;

    img {
      max-width: 598px;
      width: 100%;
    }

    ${breakpoint.TABLET} {
      position: relative;
      bottom: var(--sizing-sm);
      order: 2;
    }
  }

  ${({ isNextClicked }) =>
    isNextClicked &&
    css`
      .animation {
        opacity: 1;
        animation-name: ${FadeOutUpKeyframes};
        animation-duration: 1s;
        animation-delay: 0s;
      }
    `}
`;

export const HeroContent = styled.section<HeroContentProps>`
  z-index: 10;
  position: relative;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;

  ${breakpoint.TABLET} {
    justify-content: center;
    text-align: left;
    margin-bottom: 0;
    max-width: 615px;
  }

  p {
    opacity: 0;
    animation: ${FadeInDownKeyframes} 1s 1.25s forwards;
    color: var(--colors-gray-black);
    font-size: 1.5rem;
    line-height: 1.5;
    font-weight: 300;
    margin-bottom: 1.5rem;
  }

  h1 {
    font-weight: 400 !important;
    font-size: 2.75rem;
    color: #284e4b;
    opacity: 0;
    animation: ${FadeInDownKeyframes} 1s 1.25s forwards;
    margin: 0 auto 1.5rem;

    ${breakpoint.TABLET} {
      margin: 0 0 1.5rem;
      font-size: 3.75rem;
    }

    > span {
      background: linear-gradient(92deg, var(--colors-art-fuchsia) 1.94%, var(--colors-art-orange) 73.29%);
      background-clip: text;
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }
  }

  .alex-id-warning {
    opacity: 0;
    animation: ${FadeInLeftKeyframes} 1s 1.75s forwards;
    margin-top: var(--sizing-lg);
    width: 100%;
    background: rgba(255, 0, 0, 0.4);
    padding: 20px 20px;

    ${breakpoint.TABLET} {
      margin-top: var(--sizing-xl);
    }
  }

  .cta-message {
    padding-bottom: 20px;
  }

  ${({ isNextClicked }) =>
    isNextClicked &&
    css`
      h1,
      p,
      .cta-buttons,
      .alex-id-warning {
        opacity: 1;
        animation-name: ${FadeOutUpKeyframes};
        animation-duration: 1s;
        animation-delay: 0s;
      }
    `}
`;
